import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Checkbox from "../../Components/checkbox/Checkbox";
import ApiDropdown from "../../Components/DropDown/Apidropdown";
import Input from "../../Components/input/input";
import config from "../../config";
import "../../Style/IncidentAdd.css";
import { useFetchFullName } from "../Incidents/Hooks/useFetchFullName";
import { KnowledgeBase, initialKnowledgeBase } from "./KnowledgeBase";
import TaskGridLookup from "../../Components/Lookup/Lookup";
import { ChangeData } from "../../utils/Change/changeTypes";
import { KnowledgeBaseData } from "../../utils/KnowledgeBase/KnowledgeBaseTypes";
import { TaskData } from "../../utils/Task/taskTypes";
import { IncidentData } from "../../utils/Incident/incidentTypes";
import { initialTask, Task } from "../Task/Task";
import FileUploader from "../../Components/FileUpload/FileUpload";
import TextArea from "../../Components/TextArea/TextArea";
import { ProblemData } from "../../utils/Problem/problemTypes";
import { authFetch } from "../../utils/Security/authFetch";
import { RmaData } from "../../utils/Rma/rmaTypes";

function KnowledgeBaseEdit() {
  const [knowledgeBase, setKnowledgeBase] =
    useState<KnowledgeBase>(initialKnowledgeBase);
  const navigate = useNavigate();
  const organization = localStorage.getItem("organization") || undefined;

  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const [task, setTask] = useState<Task>(initialTask);
  const { id } = useParams();
  const [emails, setEmails] = useState<[]>();
  const [network, setNetwork] = useState<
    string | number | (string | number)[] | null
  >(null);
  const [site, setSite] = useState<
    string | number | (string | number)[] | null
  >(null);

  const [customer, setCustomer] = useState<
    string | number | (string | number)[] | undefined
  >(undefined);

  const [isTaskLookupOpen, setIsTaskLookupOpen] = useState(false);
  const handleOpenTaskLookup = () => setIsTaskLookupOpen(true);
  const handleCloseTaskLookup = () => setIsTaskLookupOpen(false);

  const [selectedTask, setSelectedTask] = useState<
    (
      | TaskData
      | ChangeData
      | RmaData
      | KnowledgeBaseData
      | ProblemData
      | RmaData
      | IncidentData
    )[]
  >([]);
  useEffect(() => {
    if (id) {
      authFetch(`${config.apiBaseUrl}/api/knowledgeBases/${id}`)
        .then((res) => res.json())
        .then((data) => setKnowledgeBase(data))
        .catch((err) => console.error("Failed to fetch knowledgeBases:", err));
    }
  }, [id]);
  useEffect(() => {
    setCustomer(knowledgeBase.affectedCustomer);
  }, [knowledgeBase.affectedCustomer]);

  useFetchFullName((fullName: string) => {
    setKnowledgeBase((prev) => ({ ...prev, user: fullName }));
  });
  const handleCancel = () => {
    navigate("/knowledge-base");
  };
  const handleDataChange = (field: keyof KnowledgeBase, value: any) => {
    setKnowledgeBase((prev) => ({ ...prev, [field]: value }));
  };
  const handleTaskSelect = (
    tasks: (
      | TaskData
      | ChangeData
      | RmaData
      | KnowledgeBaseData
      | ProblemData
      | IncidentData
    )[]
  ) => {
    const ticketIds = tasks.map((t) => t.ticketId); // collect all selected ticketIds
    handleDataChange("relatedobject", ticketIds); // now this should be an array
    setSelectedTask(tasks); // you might rename this to selectedTasks now
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    authFetch(`${config.apiBaseUrl}/api/knowledgeBases/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(knowledgeBase),
    })
      .then((res) => res.json())
      .then((data) => {
        navigate("/knowledge-base");
      })
      .catch((err) => console.error("Failed to update knowledge base:", err));
  };

  return (
    <div className="incident-form">
      <section className="form-section">
        <h3>Knowledge Base Information</h3>
        <div className="grid">
          <Input
            readOnly
            type="text"
            label="Ticket ID"
            value={knowledgeBase.ticketId || ""}
            onChange={() => {}}
          />
          <Input
            readOnly
            type="text"
            label="Status"
            value={knowledgeBase.status || ""}
            onChange={() => {}}
          />
          <Input
            type="text"
            label="Created By"
            value={knowledgeBase.createdby ?? ""}
            onChange={(val) => handleDataChange("createdby", val)}
            readOnly
          />
          <Input
            type="text"
            label="Closed By"
            value={knowledgeBase.closedby ?? ""}
            onChange={(val) => handleDataChange("createdby", val)}
            readOnly
          />
          <Input
            type="datetime-local"
            readOnly
            label="Ticket Creation Date/Time"
            value={knowledgeBase.ticketCreationDatetime}
            onChange={(val) => handleDataChange("ticketCreationDatetime", val)}
          />
          <Input
            type="datetime-local"
            label="Ticket Closure Date/Time"
            readOnly
            value={knowledgeBase.ticketClosureDatetime || ""}
            onChange={(val) => handleDataChange("ticketClosureDatetime", val)}
          />
          {isAdmin && (
            <Input
              type="text"
              label="Ticket Cross Reference Number"
              value={knowledgeBase.ticketcrossrefrencenumber || ""}
              onChange={(val) =>
                handleDataChange("ticketcrossrefrencenumber", val)
              }
            />
          )}
        </div>
      </section>

      <section className="form-section">
        <h3>Customer & Network</h3>
        <div className="grid">
          {!isAdmin && (
            <ApiDropdown
              api="affected-customers"
              labelKey="customername"
              value={organization}
              valueKey="customername"
              label="Customer"
              disabled
              isMulti
              onOpen={async () => {
                try {
                  const responseIds = await authFetch(
                    `${config.apiBaseUrl}/api/users/id-by-organization?organization=${organization}`
                  );
                  if (!responseIds.ok)
                    throw new Error(
                      `HTTP error! status: ${responseIds.status}`
                    );
                  const userIds = await responseIds.json();

                  const userIdsParam = Array.isArray(userIds)
                    ? userIds.join(",")
                    : userIds;
                  const responseEmails = await authFetch(
                    `${config.apiBaseUrl}/api/users/email-by-id?id=${userIdsParam}`
                  );
                  if (!responseEmails.ok)
                    throw new Error(
                      `HTTP error! status: ${responseEmails.status}`
                    );
                  const emails = await responseEmails.json();
                  setEmails(emails);

                  handleDataChange("foruserid", userIds);

                  handleDataChange("affectedCustomer", [organization]);
                } catch (error) {
                  console.error("Failed to fetch customer or emails:", error);
                }
              }}
            />
          )}
          {isAdmin && (
            <ApiDropdown
              api="affected-customers"
              labelKey="customername"
              valueKey="customername"
              label="Customer"
              value={customer}
              isMulti
              onChange={async (val) => {
                setCustomer(val);
                try {
                  const responseIds = await authFetch(
                    `${config.apiBaseUrl}/api/users/id-by-organization?organization=${val}`
                  );
                  if (!responseIds.ok)
                    throw new Error(
                      `HTTP error! status: ${responseIds.status}`
                    );
                  const userIds = await responseIds.json();

                  const userIdsParam = Array.isArray(userIds)
                    ? userIds.join(",")
                    : userIds;
                  const responseEmails = await authFetch(
                    `${config.apiBaseUrl}/api/users/email-by-id?id=${userIdsParam}`
                  );
                  if (!responseEmails.ok)
                    throw new Error(
                      `HTTP error! status: ${responseEmails.status}`
                    );
                  const emails = await responseEmails.json();
                  setEmails(emails);

                  handleDataChange("foruserid", userIds);
                  handleDataChange("affectedCustomer", val);
                } catch (error) {
                  console.error("Failed to fetch customer or emails:", error);
                }
              }}
            />
          )}
          {!isAdmin && organization && (
            <ApiDropdown
              api={`everything/networks?customer=${organization}`}
              labelKey="label"
              valueKey="value"
              label="Networks"
              value={knowledgeBase.affectedNetwork}
              isMulti
              onChange={(val) => {
                setNetwork(val);
                handleDataChange("affectedNetwork", val);
              }}
            />
          )}

          {!isAdmin && organization && (
            <>
              {(network || knowledgeBase.affectedNetwork) && (
                <ApiDropdown
                  api={`everything/sites?customer=${organization}&network=${
                    network || knowledgeBase.affectedNetwork
                  }`}
                  labelKey="label"
                  valueKey="value"
                  label="Site"
                  value={knowledgeBase.affectedSite}
                  isMulti
                  onChange={(val) => {
                    setSite(val);
                    handleDataChange("affectedSite", val);
                  }}
                />
              )}

              {(site || knowledgeBase.affectedSite) && (
                <ApiDropdown
                  api={`everything/links?customer=${organization}&network=${
                    network || knowledgeBase.affectedNetwork
                  }&site=${site || knowledgeBase.affectedSite}`}
                  labelKey="label"
                  valueKey="value"
                  label="Link"
                  value={knowledgeBase.affectedLink}
                  isMulti
                  onChange={(val) => handleDataChange("affectedLink", val)}
                />
              )}

              <ApiDropdown
                api={`everything/asset?customer=${organization}&network=${network}`}
                labelKey="label"
                valueKey="value"
                label="Assets"
                onChange={(val) => handleDataChange("asset", val)}
              />
            </>
          )}

          {isAdmin && (
            <>
              {(customer || knowledgeBase.affectedCustomer) && (
                <ApiDropdown
                  api={`everything/networks?customer=${
                    customer || knowledgeBase.affectedCustomer
                  }`}
                  labelKey="label"
                  valueKey="value"
                  label="Networks"
                  value={knowledgeBase.affectedNetwork}
                  isMulti
                  onChange={(val) => {
                    setNetwork(val);
                    handleDataChange("affectedNetwork", val);
                  }}
                />
              )}

              {(network || knowledgeBase.affectedNetwork) && (
                <ApiDropdown
                  api={`everything/sites?customer=${
                    customer || knowledgeBase.affectedCustomer
                  }&network=${network || knowledgeBase.affectedNetwork}`}
                  labelKey="label"
                  valueKey="value"
                  value={knowledgeBase.affectedSite || ""}
                  label="Site"
                  isMulti
                  onChange={(val) => {
                    setSite(val);
                    handleDataChange("affectedSite", val);
                  }}
                />
              )}

              {(site || knowledgeBase.affectedSite) && (
                <ApiDropdown
                  api={`everything/links?customer=${
                    customer || knowledgeBase.affectedCustomer
                  }&network=${network || knowledgeBase.affectedNetwork}&site=${
                    site || knowledgeBase.affectedSite
                  }`}
                  labelKey="label"
                  valueKey="value"
                  label="Link"
                  value={knowledgeBase.affectedLink}
                  isMulti
                  onChange={(val) => handleDataChange("affectedLink", val)}
                />
              )}

              <ApiDropdown
                api={`everything/asset?customer=${customer}&network=${network}`}
                labelKey="label"
                valueKey="value"
                label="Assets"
                onChange={(val) => handleDataChange("asset", val)}
              />
            </>
          )}
        </div>
      </section>
      <section className="form-section">
        <h3>Knowledge Base Details</h3>
        <div className="grid2">
          <TextArea
            label="Summary"
            value={knowledgeBase.summary || ""}
            onChange={(val) => handleDataChange("summary", val)}
            style={{
              minHeight: "30px",
              minWidth: "90%",
              backgroundColor: "white",
            }}
          />
          <TextArea
            label="Description"
            value={knowledgeBase.description || ""}
            onChange={(val) => handleDataChange("description", val)}
            style={{
              minHeight: "100px",
              minWidth: "90%",
              backgroundColor: "white",
            }}
          />{" "}
          <TextArea
            label="Solution"
            value={knowledgeBase.solution || ""}
            style={{
              minHeight: "100px",
              minWidth: "90%",
              backgroundColor: "white",
            }}
            onChange={(val) => handleDataChange("solution", val)}
          />
          <ApiDropdown
            api="ClosureCodes"
            labelKey="name"
            valueKey="name"
            value={knowledgeBase.closurecode}
            label="Closure Code"
            onChange={(val) => handleDataChange("closurecode", val)}
          />
          <div style={{ paddingTop: "30px" }}>
            <TextArea
              label="Root Cause"
              value={knowledgeBase.rootcause}
              onChange={(val) => handleDataChange("rootcause", val)}
              style={{
                minHeight: "100px",
                minWidth: "90%",
                backgroundColor: "white",
              }}
            />
          </div>
        </div>
      </section>
      <section className="form-section">
        <h3>Related object</h3>
        <div className="grid">
          <></>
          <FileUploader
            value={knowledgeBase.filePath}
            onRemove={(path) =>
              handleDataChange("filePath", (knowledgeBase.filePath || []).filter((p: any) => p !== path))
            }
            onChange={async (files, paths) => {
              if (!files || !paths.length) return;

              const uploadedPaths: string[] = [];

              for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append("file", files[i]);

                try {
                  const response = await authFetch(
                    `${config.apiBaseUrl}/api/incidents/upload`,
                    {
                      method: "POST",
                      body: formData,
                    }
                  );

                  if (response.ok) {
                    const { path } = await response.json();
                    uploadedPaths.push(path);
                  } else {
                    console.error(`Upload failed for file: ${files[i].name}`);
                  }
                } catch (error) {
                  console.error("Error uploading file", error);
                }
              }

              if (uploadedPaths.length) {
                handleDataChange("filePath", [
                  ...(knowledgeBase.filePath || []),
                  ...uploadedPaths,
                ]);
              }
            }}
          />
          {isAdmin && (
            <>
              <button onClick={handleOpenTaskLookup}>
                Select Related Ticket
              </button>
              {selectedTask && selectedTask.length > 0 && (
                <div>
                  <p>
                    <strong>Selected Tasks:</strong>
                  </p>
                  <ul>
                    {selectedTask.map((task, index) => (
                      <li key={index}>{task.ticketId}</li>
                    ))}
                  </ul>
                </div>
              )}
              <TaskGridLookup
                isOpen={isTaskLookupOpen}
                onClose={handleCloseTaskLookup}
                onSelect={handleTaskSelect}
              />{" "}
            </>
          )}
        </div>
      </section>
      <div className="submit-section">
        <button onClick={handleSubmit}>Submit KnowledgeBase</button>
        <button type="button" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default KnowledgeBaseEdit;
